import { NavItem } from './nav-item/nav-item';

/**
 * Módulos del ERP. El menú lateral muestra solo los items del módulo activo, que se deduce de la URL
 * por `prefijos`. El lanzador del header (ModuloSwitcherComponent) cambia de módulo navegando a `inicio`.
 */
export interface Modulo {
  id: 'comercial' | 'obras' | 'transporte';
  nombre: string;
  descripcion: string;
  /** Ícono Tabler (angular-tabler-icons), se redibuja al cambiar de módulo. */
  icono: string;
  /** Color de acento del módulo (lanzador, botón del header y etiqueta del lateral). */
  color: string;
  inicio: string;
  prefijos: string[];
  items: NavItem[];
}

export const MODULOS: Modulo[] = [
  {
    id: 'comercial',
    nombre: 'Comercial',
    descripcion: 'Ventas, compras, inventario y finanzas',
    icono: 'shopping-cart',
    color: '#5d87ff',
    inicio: '/dashboard',
    prefijos: ['/dashboard', '/catalogos', '/inventario', '/suministros', '/punto-venta', '/ventas', '/finanzas'],
    items: [
      {
        displayName: 'Tablero comercial',
        iconName: 'solar:widget-add-line-duotone',
        route: '/dashboard',
      },
      {
        displayName: 'Ventas',
        iconName: 'solar:cart-5-line-duotone',
        children: [
          { displayName: 'Punto de venta', iconName: 'solar:cart-5-line-duotone', route: '/punto-venta/venta' },
          { displayName: 'Turnos de caja', iconName: 'solar:wallet-money-line-duotone', route: '/punto-venta/turnos' },
          { displayName: 'Documentos de venta', iconName: 'solar:bill-list-line-duotone', route: '/ventas/documentos' },
        ],
      },
      {
        displayName: 'Compras e inventario',
        iconName: 'solar:box-line-duotone',
        children: [
          { displayName: 'Documentos de compra', iconName: 'solar:documents-line-duotone', route: '/suministros/documentos' },
          { displayName: 'Existencias y kardex', iconName: 'solar:box-minimalistic-line-duotone', route: '/inventario/existencias' },
          { displayName: 'Conversiones', iconName: 'solar:transfer-horizontal-line-duotone', route: '/inventario/conversiones' },
        ],
      },
      {
        displayName: 'Finanzas',
        iconName: 'solar:dollar-minimalistic-line-duotone',
        children: [
          { displayName: 'Cuentas por cobrar', iconName: 'solar:hand-money-line-duotone', route: '/finanzas/cxc' },
          { displayName: 'Cuentas por pagar', iconName: 'solar:card-send-line-duotone', route: '/finanzas/cxp' },
          { displayName: 'Gasto por centro de costo', iconName: 'solar:pie-chart-2-line-duotone', route: '/finanzas/gastos' },
        ],
      },
      {
        displayName: 'Catálogos',
        iconName: 'solar:folder-with-files-line-duotone',
        children: [
          { displayName: 'Productos', iconName: 'solar:box-minimalistic-line-duotone', route: '/catalogos/productos' },
          { displayName: 'Clientes', iconName: 'solar:users-group-rounded-line-duotone', route: '/catalogos/clientes' },
          { displayName: 'Proveedores', iconName: 'solar:delivery-line-duotone', route: '/catalogos/proveedores' },
          { displayName: 'Almacenes', iconName: 'solar:box-line-duotone', route: '/catalogos/almacenes' },
        ],
      },
    ],
  },
  {
    id: 'obras',
    nombre: 'Obras',
    descripcion: 'Obras, agrupadores y conceptos',
    icono: 'building-skyscraper',
    color: '#ffae1f',
    inicio: '/obras',
    prefijos: ['/obras', '/agrupadores', '/conceptos'],
    items: [
      { displayName: 'Obras', iconName: 'solar:buildings-2-line-duotone', route: '/obras' },
      { displayName: 'Agrupadores', iconName: 'solar:layers-line-duotone', route: '/agrupadores' },
      { displayName: 'Conceptos', iconName: 'solar:list-check-line-duotone', route: '/conceptos' },
      { navCap: 'Configuración' },
      { displayName: 'Catálogos', iconName: 'solar:settings-line-duotone', route: '/obras/catalogos' },
    ],
  },
  {
    id: 'transporte',
    nombre: 'Transporte',
    descripcion: 'Flota, operadores y mantenimiento',
    icono: 'bus',
    color: '#13deb9',
    inicio: '/transporte/unidades',
    prefijos: ['/transporte'],
    items: [
      { displayName: 'Alertas', iconName: 'solar:bell-bing-line-duotone', route: '/transporte/alertas' },
      { navCap: 'Flota' },
      { displayName: 'Unidades', iconName: 'solar:bus-line-duotone', route: '/transporte/unidades' },
      { displayName: 'Operadores', iconName: 'solar:user-id-line-duotone', route: '/transporte/operadores' },
      { displayName: 'Asignaciones', iconName: 'solar:link-round-angle-line-duotone', route: '/transporte/asignaciones-unidad' },
      { navCap: 'Mantenimiento' },
      { displayName: 'Reportes de falla', iconName: 'solar:danger-triangle-line-duotone', route: '/transporte/reportes-falla' },
      { displayName: 'Mantenimientos', iconName: 'solar:settings-line-duotone', route: '/transporte/mantenimientos' },
      { displayName: 'Autorizaciones', iconName: 'solar:shield-check-line-duotone', route: '/transporte/autorizaciones' },
      { navCap: 'Configuración' },
      { displayName: 'Catálogos', iconName: 'solar:list-check-line-duotone', route: '/transporte/catalogos' },
    ],
  },
];

/** Pantallas (hojas con ruta) de un módulo, aplanando los grupos; `grupo` = nombre del grupo padre. */
export function pantallasDe(modulo: Modulo): (NavItem & { grupo?: string })[] {
  const hojas: (NavItem & { grupo?: string })[] = [];
  const recorrer = (items: NavItem[], grupo?: string) =>
    items.forEach((i) => {
      if (i.children?.length) recorrer(i.children, i.displayName);
      else if (i.route && i.displayName) hojas.push({ ...i, grupo });
    });
  recorrer(modulo.items);
  return hojas;
}
